import { useState } from "react";
import { Lightning, CaretDown, CaretUp, Warning } from "@phosphor-icons/react";

// Jul 2026 (Randy) — tiny per-silo "AI Credit" chip for the dashboard card.
// Sits above the Manage/Hide/Open row. Tap to reveal the three platform
// voucher pools. If any pool (that isn't covered by the creator's own key)
// falls below LOW_CREDIT_CENTS, the chip turns amber, auto-expands, and the
// offending pools are highlighted red.
export const LOW_CREDIT_CENTS = 25;

const PROVIDERS = [
  { key: "gemini", label: "Gemini", sub: "text" },
  { key: "openai", label: "OpenAI", sub: "voice" },
  { key: "anthropic", label: "Claude", sub: "reasoning" },
];

const fmt = (cents) => `$${(Math.max(0, cents || 0) / 100).toFixed(2)}`;

export default function SiloCreditChip({ balances = {}, byok = {}, ghost = {}, onTopUp, testidSuffix = "" }) {
  const rows = PROVIDERS.map((p) => {
    const cents = Number(balances[p.key] || 0);
    const onOwnKey = !!byok[p.key];
    const low = !onOwnKey && cents < LOW_CREDIT_CENTS;
    // Oct 2026 — the gifted slice of this bucket. Trial credit reads blue, a
    // comp gift reads purple. Both are spendable and neither is cash-out-able,
    // so the creator should never mistake them for their own money.
    const g = ghost[p.key] || {};
    const ghostCents = Number(g.cents || 0);
    const ghostKind = (g.sources || []).includes("trial") ? "trial" : ((g.sources || []).length ? "gift" : null);
    return { ...p, cents, onOwnKey, low, ghostCents, ghostKind };
  });
  const anyLow = rows.some((r) => r.low);
  const anyGhost = rows.some((r) => r.ghostCents > 0);
  // Oct 2026 (Randy) — *"this little drawer keeps opening up by default… it
  // needs to remain closed and not automatically be open again next time they
  // reverse to this page."* The auto-open was `useState(anyLow)`, and because
  // the dashboard remounts this component on every visit, closing it never
  // stuck: a creator running on Gemini alone met the same drawer every time.
  // Now a close is remembered for the browser session — per silo, and also
  // silenced by dismissing the dashboard-wide banner, since that is the same
  // warning said louder.
  const closedKey = `mysilo_credit_drawer_closed:${testidSuffix}`;
  const [open, setOpen] = useState(() => {
    if (!anyLow) return false;
    try {
      if (sessionStorage.getItem("mysilo_low_credit_dismissed") === "1") return false;
      if (sessionStorage.getItem(closedKey) === "1") return false;
    } catch { /* private mode — fall through to the old behaviour */ }
    return true;
  });

  const toggle = () => setOpen((v) => {
    if (v) { try { sessionStorage.setItem(closedKey, "1"); } catch { /* private mode */ } }
    return !v;
  });

  return (
    <div className="relative inline-block" data-testid={`silo-credit-chip-${testidSuffix}`}>
      <button
        type="button"
        onClick={toggle}
        data-testid={`silo-credit-toggle-${testidSuffix}`}
        title={anyGhost
          ? "AI Credit — includes credit we gave you, billed at cost while it lasts"
          : "AI Credit — platform voucher pools for this silo"}
        className={`text-[9px] sm:text-[10px] uppercase tracking-[0.14em] px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-md flex items-center gap-1 font-medium transition-colors border ${
          anyLow
            ? "bg-[#FDF1ED] text-[#C16E5A] border-[#C16E5A]/40 hover:bg-[#FCE8E0]"
            : anyGhost
              ? "bg-[#E8EBE5] text-[#4A5D4E] border-[#4A5D4E]/30 hover:bg-[#DCE3D8]"
              : "bg-[#F0F1EE] text-[#4A5D4E] border-transparent hover:bg-[#E5E5E0]"
        }`}
      >
        {anyLow ? <Warning size={11} weight="fill" /> : <Lightning size={11} weight="duotone" />}
        <span>AI Credit</span>
        {open ? <CaretUp size={9} weight="bold" /> : <CaretDown size={9} weight="bold" />}
      </button>

      {open && (
        <div
          className="absolute left-0 top-full mt-1 z-30 w-52 rounded-lg border hairline bg-white shadow-lg p-2.5 space-y-1.5"
          data-testid={`silo-credit-dropdown-${testidSuffix}`}
        >
          {rows.map((r) => (
            <div
              key={r.key}
              data-testid={`silo-credit-row-${r.key}-${testidSuffix}`}
              className={`flex items-center justify-between text-[11px] rounded-md px-1.5 py-1 ${
                r.low ? "bg-[#FDF1ED]" : ""
              }`}
            >
              <span className={`flex items-baseline gap-1 ${r.low ? "text-[#C16E5A] font-semibold" : "text-[#1C1B1A]"}`}>
                {r.label}
                <span className="text-[9px] text-[#9A9995] uppercase tracking-wide">{r.sub}</span>
              </span>
              {r.onOwnKey ? (
                <span className="text-[10px] text-[#2F6B3A] font-medium">Own key ✓</span>
              ) : (
                <span className={`tabular-nums font-medium flex items-center gap-1 ${
                  r.low ? "text-[#C16E5A]" : (r.ghostCents > 0 ? (r.ghostKind === "trial" ? "text-[#4A5D4E]" : "text-[#6B4A8A]") : "text-[#4A5D4E]")
                }`}>
                  {r.low && <span className="text-[8px] uppercase tracking-wide bg-[#C16E5A] text-white rounded px-1 py-px">Low</span>}
                  {r.ghostCents > 0 && (
                    <span
                      data-testid={`silo-credit-ghost-${r.key}-${testidSuffix}`}
                      title={r.ghostKind === "trial"
                        ? `${fmt(r.ghostCents)} of this is trial credit — billed at cost (1×), and it ends with your trial`
                        : `${fmt(r.ghostCents)} of this is credit we gifted you — billed at cost (1×)`}
                      className={`text-[8px] uppercase tracking-wide text-white rounded px-1 py-px ${
                        r.ghostKind === "trial" ? "bg-[#4A5D4E]" : "bg-[#6B4A8A]"
                      }`}
                    >
                      {r.ghostKind === "trial" ? "Trial" : "Gift"} {fmt(r.ghostCents)}
                    </span>
                  )}
                  {fmt(r.cents)}
                </span>
              )}
            </div>
          ))}
          {onTopUp && (
            <button
              type="button"
              onClick={() => { onTopUp(); setOpen(false); }}
              data-testid={`silo-credit-topup-${testidSuffix}`}
              className="w-full mt-1 text-[10px] uppercase tracking-[0.14em] px-2 py-1.5 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] font-medium"
            >
              Top up credit
            </button>
          )}
        </div>
      )}
    </div>
  );
}
